import React, { useState, useEffect } from 'react';
import { useOktaAuth } from "@okta/okta-react";
import { Navigate, useNavigate } from 'react-router-dom';
import StackedContent from "@components/StackedContent/StackedContent";
import PageNavigation from '@/components/PageNavigation/PageNavigation';
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import Button from '@components/Button/Button';
import StatusMarker from '@components/StatusMarker/StatusMarker';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useTasklist } from '@/context/TasklistContext';
import { useLinksContext } from '@context/LinksContext';
import { openChat } from '@/utils/linksUtils';
import { getTasklistId, getTasklistNavigationMeta } from '../../utils/tasklistLegacyNavigation';
import config from '@/config';
import {RedirectIcon, DownloadIcon} from '@/components/MenusIcons/MenusIcons';
import heroUnion from '@assets/icons/page-key.svg';

const SchoolDocuments = () => {
    const { userRoleVal } = useOverviewContext();
    const { selectedStudent } = useSelectedStudent();
    const schoolId = selectedStudent?.schoolId || '';
    const displayName = selectedStudent?.nickName || selectedStudent?.firstName || '';
    const SchoolAcronym = selectedStudent?.schoolShortName || '';
    const age = selectedStudent?.age;

    const { tasklistData } = useTasklist();
    const checklistId = getTasklistId(tasklistData, 'SchoolDocuments');
    
    const isReadyToRender =
        selectedStudent?.accountId !== undefined &&
        selectedStudent?.accountId !== null &&
        typeof checklistId === 'number';

    const enrollmentLink = config.LINKS.ENROLLMENT_PORTAL;
    const consentLink = config.LINKS.CONSENT_URL;

    // Get IdentityId from Okta Storage
      const { authState } = useOktaAuth();
      const identityId =
        authState?.isAuthenticated && authState.idToken?.claims?.identityId
          ? String(authState.idToken.claims.identityId)
          : "none";

    // Page Navigation
    const navigate = useNavigate();
    const {
    currentIndex,
    totalTasks,
    previousSubtitle,
    nextSubtitle,
    nextSlug,
    prevSlug,
    } = getTasklistNavigationMeta(tasklistData, window.location.pathname, selectedStudent?.schoolId);
    
    const handleNext = () => {
      if (nextSlug) {
        navigate(`/tasklist/${nextSlug}`);
      }
    };
    
    const handlePrevious = () => {
      if (prevSlug) {
        navigate(`/tasklist/${prevSlug}`);
      }
    };
    
    const breadcrumbItems = [
        { title: "Home", path: "/" },
        { title: 'School prep tasklist', path: '/tasklist' },
        { title: "Important Start-of-School Documents" }
    ];

    // Acknowledgement Link
    const { acknowledgement, fetchAcknowledgement } = useLinksContext();
    const [ackLoading, setAckLoading] = useState(true);
    const [ackError, setAckError] = useState(false);

    useEffect(() => {
        const fetchAck = async () => {
            if (selectedStudent?.schoolId && selectedStudent?.schoolShortName) {
                setAckLoading(true);
                setAckError(false);
                try {
                    await fetchAcknowledgement(selectedStudent?.schoolId, selectedStudent.schoolShortName);
                } catch (err) {
                    setAckError(true);
                } finally {
                    setAckLoading(false);
                }
            }
        };
        fetchAck();
    }, [selectedStudent?.schoolId, selectedStudent?.schoolShortName]);

    return (
        <>
        <div className="school-start-documents">
            <div className="sub-page-hero">
                <Breadcrumb items={breadcrumbItems} />
                <SubPageHero
                    theme="primary"
                    title="Important Start-of-School Documents"
                    description="Ensure a smoother transition by submitting any forms and documents required for your student."
                    image={heroUnion}
                />
            </div>

            <StackedContent>
                <StackedContent.Col1 cols="4">
                    <h2>Enrollment Portal Documents</h2>
                </StackedContent.Col1>
                <StackedContent.Col2 cols="6">
                    <div className='content-wrapper'>
                        <div className='text-block'>
                            <p className='body-m fw-700'>Your enrollment is approved, but critical documents are still required.</p>
                            <p className='body-m'>Remember to check and see if you need to take any further action now or after the start of school.</p>
                        </div>
                        <Button
                            label="Log In to the Enrollment Portal"
                            icon={< RedirectIcon />}
                            iconPosition="after"
                            size="lg"
                            variant="border"
                            width="fit-content"
                            color="primary"
                            breakpoint="all"
                            linkUrl={enrollmentLink}
                            linkTarget="_blank"
                            className="school-btn"
                        /> 
                    </div>
                </StackedContent.Col2>
            </StackedContent>

        {typeof age === 'number' && age < 13 && (
            <StackedContent>
                <StackedContent.Col1 cols="4">
                    <h2>Consent Forms for {displayName}</h2>
                </StackedContent.Col1>
                <StackedContent.Col2 cols="6">
                    <div className='content-wrapper'>
                        <div className='text-block'>
                            <p className='body-m'>Make sure your student's consent is up-to-date. Parent/legal guardian permission is required for participation in National K12 Zone and National K12 Chat.</p>
                            <p className='body-m'><span className='fw-700'>Consent can only be provided by legal guardians. Not your student's legal guardian? No need to worry -we'll reach out to them directly.</span></p>
                        </div>
                        <Button
                            label="Fill Out Forms"
                            icon={< RedirectIcon />}
                            iconPosition="after"
                            size="lg"
                            variant="border"
                            width="fit-content"
                            color="primary"
                            breakpoint="all"
                            linkUrl={consentLink}
                            linkTarget="_blank"
                            className="school-btn"
                        /> 
                    </div>
                </StackedContent.Col2>
            </StackedContent>
        )}

        {/* Acknowledgement of School Policies */}
            <StackedContent>
                <StackedContent.Col1 cols="4">
                    <h2>Acknowledgement of School Policies and Expectations</h2>
                </StackedContent.Col1>
                <StackedContent.Col2 cols="6">
                    <div className='content-wrapper'>
                        <div className='text-block'>
                            <p className='body-m'>Access {SchoolAcronym}’s policies overview and requirements here.</p>
                        </div>
                        <Button
                            label="Download Acknowledgement"
                            icon={< DownloadIcon />}
                            iconPosition="before"
                            size="lg"
                            variant="border"
                            width="fit-content"
                            color="primary"
                            breakpoint="all"
                            linkUrl={acknowledgement?.acknowledgementDocumentLink || ''}
                            linkTarget="_blank"
                            className="school-btn"
                            disabled={!acknowledgement?.acknowledgementDocumentLink || ackLoading || ackError}
                        />
                        {/* Error Message */}
                        {(!acknowledgement?.acknowledgementDocumentLink && !ackLoading) && (
                            <p className="ack-error body-m">
                                This document is currently unavailable. Please try reloading the page or come back later. If it won’t load, try{' '}
                                <a 
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); openChat(identityId); }}
                                    rel="noopener noreferrer"
                                >
                                    contacting support
                                </a>.
                            </p>
                        )}
                    </div>
                </StackedContent.Col2>
            </StackedContent>
            
            {isReadyToRender && (
                <StatusMarker
                    accountId={selectedStudent!.accountId}
                    checklistId={checklistId}
                />
            )}

            <section className="navigation">
                <StackedContent>
                    <StackedContent.Col1 cols="10">
                        <PageNavigation
                        currentIndex={currentIndex}
                        totalTasks={totalTasks}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        previousLabel="Previous Task"
                        nextLabel="Next Task"
                        previousSubtitle={previousSubtitle}
                        nextSubtitle={nextSubtitle}
                    />               
                    </StackedContent.Col1>
                </StackedContent>
            </section>
        </div>
        </>
    );
};

export default SchoolDocuments;